import React, { useState, useEffect, useContext } from "react";
import {
  AppBar,
  Typography,
  Button,
  IconButton,
  TextField,
  Box,
  Breadcrumbs,
  Autocomplete,
  InputAdornment,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { useTheme } from "@mui/material/styles";
import { Reasontypedata } from "../../components/grid/GridData.tsx";
import { externalgetApi, externalpostApi } from "../../services/externalApis.tsx";
import {
  SETTING_API_16,
  SETTING_API_17,
} from "../../api/ApiModel.tsx";
import ViewDataGrid from "../../components/grid/ViewDataGrid.tsx";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import { usePageData } from "../../utils/common/PageDataContext.tsx";
import { CON_STATUS_ACTIVE } from "../../utils/sys/Constants.tsx";
import { useSelector } from "react-redux";
import CommentsDisabledIcon from "@mui/icons-material/CommentsDisabled";
import SystemAlert from "../../components/common/SystemAlert.tsx";
import {
  MessageCategory,
} from "../../utils/sys/Messages.tsx";

// Types and Interfaces
interface ReasonCategory {
  reasoncatcode: string | number;
  reasoncatname: string;
  [key: string]: unknown;
}

interface ReasonType {
  reasontypecode: string | number;
  reasoncatcode?: string | number;
  descrip?: string;
  [key: string]: unknown;
}

interface UserParams {
  LOCCODE?: string;
  STAFFMAPLOCODE?: string;
  COMPCODE?: string;
  USERNAME?: string;
}

interface UserState {
  USER_PARAM?: UserParams;
}

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  onClick: (id: string | number) => void;
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

interface ErrorState {
  reasoncatcode?: string;
  [key: string]: string | undefined;
}

interface PageData {
  title: string;
  [key: string]: unknown;
}

interface StatusOption {
  label: string;
  value: string;
}

interface Column {
  field: string;
  headerName: string;
  width?: number;
  flex?: number;
}

interface RowWithId extends ReasonType {
  id: string | number;
}

function ViewReason(): React.ReactElement {
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const { setPageData } = usePageData();
  
  const SYS_USER = useSelector((state: { user: UserState }) => state.user) || {};
  const { LOCCODE, STAFFMAPLOCODE, COMPCODE, USERNAME } =
    SYS_USER.USER_PARAM || {};

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [datadoccat, setDatadoccat] = useState<ReasonCategory[] | null>(null);
  const [data, setData] = useState<ReasonType[] | null>(null);
  const [error, setError] = useState<ErrorState>({});
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<MessageCategory.SUCCESS | MessageCategory.ERROR | MessageCategory.WARNING>(MessageCategory.SUCCESS);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("A");
  const [openDisableDialog, setOpenDisableDialog] = useState<boolean>(false);
  const [disableTargetId, setDisableTargetId] = useState<string | number | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [filteredRows, setFilteredRows] = useState<ReasonType[]>([]);
  const [selectedreasoncatcode, setSelectedreasoncatcode] = useState<string | number>("");

  useEffect(() => {
    setPageData({ title: "ReasonTypes" } as PageData);
  }, [setPageData]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (!selectedreasoncatcode) return;
      try {
        setIsLoading(true);
        const result = (await externalgetApi(SETTING_API_17, "get", {
          COMPCODE,
          LOCCODE: STAFFMAPLOCODE,
          reasoncatcode: selectedreasoncatcode,
          STS: statusFilter,
        })) as ReasonType[];
        
        setData(result);
        setFilteredRows(result);
        console.log("Fetched data:", result);
        setIsLoading(false);
      } catch (err) {
        console.error(err);
        setIsLoading(false);
      }
    };
    fetchData();
  }, [selectedreasoncatcode, statusFilter, COMPCODE, STAFFMAPLOCODE]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setIsLoading(true);
        const result = (await externalgetApi(SETTING_API_16, "get", {
          STS: CON_STATUS_ACTIVE,
        })) as ReasonCategory[];
        
        setDatadoccat(result);
        console.log("Fetched doccat data:", result);
        setIsLoading(false);
      } catch (err) {
        setError(err as ErrorState);
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (Array.isArray(datadoccat) && datadoccat.length > 0) {
      setSelectedreasoncatcode(datadoccat[0].reasoncatcode);
    }
  }, [datadoccat]);

  const handleItemClick = (id: string | number, actionText: string): void => {
    if (actionText === "Edit") {
      const selectedItem = data?.find((item) => item.reasontypecode === id);
      const selectedCategory = datadoccat?.find(
        (item) => item.reasoncatcode === selectedItem?.reasoncatcode
      );
      if (selectedItem) {
        navigate("/CreatreReason", {
          state: {
            selectedItem,
            actionText,
            selectedreasoncatcode: selectedCategory?.reasoncatcode || "",
            selectedreasoncatname: selectedCategory?.reasoncatname || "",
          },
        });
      }
    }
  };

  const openDisableConfirmation = (reasontypecode: string | number): void => {
    setDisableTargetId(reasontypecode);
    setOpenDisableDialog(true);
  };

  const cancelDisable = (): void => {
    setOpenDisableDialog(false);
    setDisableTargetId(null);
  };

  const confirmDisable = (): void => {
    if (disableTargetId) handleDisable(disableTargetId);
    cancelDisable();
  };

  const handleEnable = async (reasontypecode: string | number): Promise<void> => {
    const selectedItem = data?.find(
      (item) => item.reasontypecode === reasontypecode
    );
    if (!selectedItem) return;
    
    const payload = {
      reasontypecode: selectedItem.reasontypecode,
      reasoncatcode: selectedItem.reasoncatcode,
      compcode: COMPCODE,
      loccode: STAFFMAPLOCODE,
      descrip: selectedItem.descrip,
      sts: "A",
      enuser: USERNAME,
    };
    
    await externalpostApi(SETTING_API_17, payload);
    setAlertMessage("Enabled successfully.");
    setAlertType(MessageCategory.SUCCESS);
    setAlertOpen(true);
    
    const refreshedData = (await externalgetApi(SETTING_API_17, "get", {
      COMPCODE,
      LOCCODE: STAFFMAPLOCODE,
      reasoncatcode: selectedreasoncatcode,
      STS: statusFilter,
    })) as ReasonType[];
    
    setData(refreshedData);
    setFilteredRows(refreshedData);
  };

  const handleDisable = async (reasontypecode: string | number): Promise<void> => {
    const selectedItem = data?.find(
      (item) => item.reasontypecode === reasontypecode
    );
    if (!selectedItem) return;
    
    // Using externaldeletecomApi - adjust based on your actual API
    await fetch(`Reasontype/${selectedItem.reasontypecode}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        BREASONTYPECODE: String(selectedItem.reasontypecode),
        ENUSER: USERNAME || "ss",
        sts: "D",
      }),
    });

    setAlertMessage("Disabled successfully.");
    setAlertType(MessageCategory.WARNING);
    setAlertOpen(true);
    
    const refreshedData = (await externalgetApi(SETTING_API_17, "get", {
      COMPCODE,
      LOCCODE: STAFFMAPLOCODE,
      reasoncatcode: selectedreasoncatcode,
      STS: CON_STATUS_ACTIVE,
    })) as ReasonType[];
    
    setData(refreshedData);
    setFilteredRows(refreshedData);
  };

  const handleSearchChange = (event: React.SyntheticEvent, value: string | null): void => {
    const searchValue = value || "";
    setSearchQuery(searchValue);
    const filtered = Array.isArray(data)
      ? data.filter((row) =>
          (row.descrip || "")
            .toLowerCase()
            .includes(searchValue.toLowerCase())
        )
      : [];
    setFilteredRows(filtered);
  };

  const addToView = (): void => {
    const selectedCategory = datadoccat?.find(
      (item) => item.reasoncatcode === selectedreasoncatcode
    );
    navigate("/CreatreReason", {
      state: {
        selectedreasoncatcode: selectedCategory?.reasoncatcode || "",
        selectedreasoncatname: selectedCategory?.reasoncatname || "",
        actionText: "Add",
      },
    });
  };

  const menuItems: MenuItem[] =
    statusFilter === "A"
      ? [
          {
            text: "Edit",
            icon: <BorderColorOutlinedIcon />,
            onClick: (id: string | number) => handleItemClick(id, "Edit"),
          },
          {
            text: "Disable",
            icon: <CommentsDisabledIcon />,
            onClick: (id: string | number) => openDisableConfirmation(id),
          },
        ]
      : [
          {
            text: "Enable",
            icon: <BorderColorOutlinedIcon />,
            onClick: (id: string | number) => handleEnable(id),
          },
        ];

  // Helper functions for Autocomplete
  const findSelectedReasonCategory = (): ReasonCategory | undefined => {
    if (!Array.isArray(datadoccat) || !selectedreasoncatcode) return undefined;
    return datadoccat.find((item) => item.reasoncatcode === selectedreasoncatcode);
  };

  const statusOptions: StatusOption[] = [
    { label: "Enable", value: "A" },
    { label: "Disable", value: "D" },
  ];

  const findSelectedStatus = (): StatusOption => {
    return {
      label: statusFilter === "A" ? "Enable" : "Disable",
      value: statusFilter,
    };
  };

  // Prepare rows with id for the grid
  const rowsWithId: RowWithId[] = filteredRows.map((row) => ({
    ...row,
    id: row.reasontypecode || Math.random(),
  }));

  // Placeholder processRowUpdate function
  const processRowUpdate = async (newRow: any): Promise<any> => {
    console.log("Row update attempted:", newRow);
    return newRow;
  };

  return (
    <>
      <Box sx={styles.box_breadcrumb_view}>
        <Breadcrumbs
          separator="›"
          aria-label="breadcrumb"
          sx={styles.breadcrumb}
        >
          <Link
            underline="hover"
            sx={styles.breadcrumb_link}
          >
            Home
          </Link>
          <Link
            sx={styles.breadcrumb_link}
            underline="hover"
          >
            Settings
          </Link>
          <Link
            sx={styles.breadcrumb_link_page}
            underline="hover"
            aria-current="page"
          >
            Reason Types
          </Link>
        </Breadcrumbs>
      </Box>

      <Box sx={styles.box_page_container}>
        <AppBar sx={styles.appbar}>
          <Grid container spacing={2} sx={styles.appbar_grid_container}>
            <Grid
              size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 8 }}
              sx={styles.appbar_grid_container_col_1}
            >
              <Typography sx={styles.appbar_title}>
                {Reasontypedata.name}
              </Typography>
            </Grid>

            <Grid
              size={{ xs: 12, sm: 12, md: 6, lg: 8, xl: 4 }}
              sx={styles.appbar_grid_search}
            >
              <Autocomplete
                id="act_reasoncat"
                options={Array.isArray(datadoccat) ? datadoccat : []}
                getOptionLabel={(option: ReasonCategory) => option.reasoncatname || ""}
                disableClearable
                fullWidth
                value={findSelectedReasonCategory()}
                onChange={(event: React.SyntheticEvent, newValue: ReasonCategory | null) => {
                  const reasoncatcode = newValue ? newValue.reasoncatcode : "";
                  console.log("Selected reasoncatcode:", reasoncatcode);
                  setSelectedreasoncatcode(reasoncatcode);
                }}
                renderInput={(params) => {
                  const { id, ...restParams } = params;
                  return (
                    <TextField
                      id="txt_reasoncatname"
                      {...restParams}
                      label={
                        <span>
                          Reason Category Name
                          <span style={{ color: "red" }}> *</span>
                        </span>
                      }
                      variant="outlined"
                      error={!!error?.reasoncatcode}
                      helperText={error?.reasoncatcode}
                      size="small"
                      name="reasoncatcode"
                    />
                  );
                }}
                ListboxProps={{
                  style: {
                    maxHeight: 150,
                    backgroundColor: theme.palette.background.paper,
                  },
                }}
              />

              <Autocomplete
                id="act_status"
                options={statusOptions}
                getOptionLabel={(option: StatusOption) => option.label}
                disableClearable
                fullWidth
                value={findSelectedStatus()}
                onChange={(event: React.SyntheticEvent, newValue: StatusOption | null) => {
                  if (newValue) {
                    setStatusFilter(newValue.value);
                  }
                }}
                renderInput={(params) => {
                  const { id, ...restParams } = params;
                  return (
                    <TextField
                      id="txt_status"
                      {...restParams}
                      label="Status"
                      variant="outlined"
                      size="small"
                    />
                  );
                }}
                ListboxProps={{
                  style: {
                    maxHeight: 150,
                    backgroundColor: theme.palette.background.paper,
                  },
                }}
              />

              <Autocomplete
                freeSolo
                id="act_appbarsearch"
                fullWidth
                disableClearable
                value={searchQuery}
                onInputChange={handleSearchChange}
                onChange={handleSearchChange}
                sx={styles.ipt_appbar_search_auto}
                options={data?.map((row) => row.descrip || "").filter(Boolean) || []}
                renderInput={(params) => {
                  const { id, ...restParams } = params;
                  return (
                    <TextField
                      id="txt_appbarsearch"
                      {...restParams}
                      placeholder="Search by..."
                      size="small"
                      sx={styles.ipt_appbar_search}
                      InputProps={{
                        ...restParams.InputProps,
                        type: "search",
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              id="btn_appbarsearch"
                              sx={styles.btn_appbar_search_icon}
                            >
                              <SearchIcon id="ico_search" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  );
                }}
              />

              <Button
                id="btn_add"
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                size="small"
                sx={{ ...styles.btn_primary, ...styles.btn_appbar_add }}
                onClick={addToView}
              >
                Add
              </Button>
            </Grid>
          </Grid>
        </AppBar>

        <Box sx={styles.view_page_container}>
          <ViewDataGrid
            columns={Reasontypedata.columns}
            rows={rowsWithId}
            menuItems={menuItems}
            tabletitle={Reasontypedata.name}
            gridid="reasontypecode"
            processRowUpdate={processRowUpdate}
          />
        </Box>

        <SystemAlert
          open={alertOpen}
          type={alertType}
          message={alertMessage}
          onClose={() => setAlertOpen(false)}
        />

        {/* Disable Confirmation Dialog */}
        <Dialog
          id="dlg_confirmdisabl"
          open={openDisableDialog}
          onClose={cancelDisable}
          aria-labelledby="disable-dialog-title"
        >
          <DialogTitle id="disable-dialog-title">Confirm Disable</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to disable this reason type?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              id="btn_canceldisable"
              onClick={cancelDisable}
              sx={{ ...styles.btn_secondary, ...styles.btn_add_cancel }}
              color="inherit"
            >
              Cancel
            </Button>
            <Button
              id="btn_confirmdisable"
              onClick={confirmDisable}
              color="error"
              sx={{ ...styles.btn_primary, ...styles.btn_add_confirm }}
              variant="contained"
            >
              Disable
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}

export default ViewReason;